"use client";

import { motion, useReducedMotion } from "framer-motion";
import { CircleHelp, ArrowRight, MessageSquare, CircleCheck as CheckCircle2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqs: FaqItem[] = [{
  id: "item-1",
  question: "How long does international SEO take to show results?",
  answer: "Timing depends on competitive density in your target markets, the number of countries and languages, existing site health, and how quickly technical recommendations and localized content ship. Early signals such as crawl activity, indexing of regional pages, and initial impressions in target countries appear first, while commercial outcomes like consistent organic enquiries and revenue follow later. I provide realistic expectations based on your specific scope; nobody can guarantee search rankings."
}, {
  id: "item-2",
  question: "Should I use ccTLDs, subdomains or subdirectories?",
  answer: "Each architecture has trade-offs and there is no universal best choice. Dedicated ccTLDs provide the clearest geographic signal to users and search engines but require building authority separately for every domain at higher administrative and registration cost. Subdirectories share the root domain's accumulated authority and are simpler to maintain, though regional separation is less distinct. Subdomains sit in between. I evaluate your technical stack, budget, brand positioning, and operational resources to choose the right approach with you."
}, {
  id: "item-3",
  question: "How do you handle hreflang?",
  answer: "Hreflang tells search engines which language and regional alternatives exist for a given piece of content, though it does not guarantee which URL is displayed. Implementation requires reciprocal return links across all versions, valid ISO language and region codes, and an x-default fallback for unsegmented visitors. Hreflang tags can be placed in HTML head tags, HTTP headers, or XML sitemaps, and must remain completely consistent with canonical tags so pages self-canonicalise."
}, {
  id: "item-4",
  question: "Do I need uniquely localised content and native speakers for every region?",
  answer: "Localisation is not simple translation. It requires adapting terminology, search intent, currencies, measurement units, regulatory details, tone of voice, and local payment or shipping preferences. While native-speaker copywriters are ideal, a practical and effective starting point is having professional translators work directly from structured SEO content briefs that I prepare with target keywords and regional intent mapping."
}, {
  id: "item-5",
  question: "Can you help if we already have multiple country websites?",
  answer: "Yes. When organisations already run multiple country websites, they often suffer from cross-market keyword cannibalisation, conflicting hreflang tags, duplicate product copy, or redundant pages that confuse search engines. I audit existing domain setups, map URL overlap, resolve canonical and tag conflicts, and recommend a streamlined architecture that consolidates search performance."
}, {
  id: "item-6",
  question: "Will international expansion hurt my existing national rankings?",
  answer: "Managed carefully, international expansion need not harm your domestic search performance. Clean URL architecture, self-referencing canonical tags, and accurate hreflang implementation substantially reduce cross-market conflicts by clarifying to search engines which version serves each region. However, no search outcome can be unconditionally guaranteed, so I thoroughly assess the risks and structural dependencies for your specific website before any rollout begins."
}, {
  id: "item-7",
  question: "How do you handle server locations and CDNs?",
  answer: "CDN and server infrastructure should be chosen for website speed, security, and user experience rather than relying on server location as a geotargeting signal. Modern search engines evaluate language and architecture signals over server IP. Crucially, automatic IP-based redirects should be avoided because they can prevent search crawlers and users from reaching alternate regional versions; a clear, crawlable language selector with clean regional XML sitemaps is preferred."
}, {
  id: "item-8",
  question: "Is international SEO worth it for a small business?",
  answer: "It can be, provided there is verified search demand in your target countries and your business has the operational capacity to fulfil orders, handle local customer service, and process payments there. Before committing significant budget, I examine analytics and Search Console data to check existing organic interest from overseas and recommend starting with one or two high-opportunity markets rather than attempting global expansion all at once."
}, {
  id: "item-9",
  question: "How are international SEO services scoped and priced?",
  answer: "Fees depend on the number of target countries and languages, the complexity of your website architecture, CMS platform constraints, and whether you require an initial audit and strategy, hands-on implementation support, or ongoing advisory. I scope every proposal transparently around your exact requirements, and businesses often start with a comprehensive SEO audit before rolling out multi-market work."
}, {
  id: "item-10",
  question: "How does multinational SEO work for organisations with several regional marketing teams?",
  answer: "In multi-regional organisations, central technical governance is essential. I establish a shared architecture, canonical, and hreflang framework so that core technical rules are consistent across markets. At the same time, I coordinate with local teams on regional keyword nuances, localized search intent, and native terminology, providing clear playbooks so in-house teams can execute without causing cross-border cannibalisation."
}];
export default function Internationalseofaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
  return <section id="internationalseofaq" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      {/* FAQPage JSON-LD schema matching visible questions and answers */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />

      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-40">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-secondary/50 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-accent/10 blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs tracking-wide uppercase">
            <CircleHelp className="size-3.5 text-primary" />
            <span>Common questions</span>
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-serif">
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Straightforward answers to the most common questions about scaling your organic visibility across borders and international search markets.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="lg:col-span-8 space-y-4">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-4">
              {faqs.map((faq, i) => <motion.div key={faq.id} variants={itemVariants} data-index={i}>
                  <AccordionItem value={faq.id} className="border border-border bg-card text-card-foreground rounded-xl px-6 py-1 shadow-xs transition-colors duration-200 hover:border-primary/30">
                    <AccordionTrigger className="text-base sm:text-lg font-medium text-foreground py-5 hover:no-underline hover:text-primary transition-colors text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed pt-1 pb-5 font-sans">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-4 lg:sticky lg:top-28">
            <div className="bg-card text-card-foreground border border-border rounded-2xl p-6 sm:p-8 shadow-md relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-bl-full pointer-events-none" />

              <div className="inline-flex items-center justify-center p-3 rounded-xl bg-primary/10 text-primary mb-6">
                <MessageSquare className="size-6" />
              </div>

              <h3 className="text-xl font-semibold text-foreground font-serif mb-3">
                Have a question about your target markets?
              </h3>

              <p className="text-sm text-muted-foreground font-sans leading-relaxed mb-6">
                Discuss your target regions, site structure, and international goals with me.
              </p>

              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-2.5 text-xs sm:text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Multi-market &amp; language coverage</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs sm:text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Tailored multi-market roadmap</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs sm:text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Hreflang and architecture guidance</span>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <Button asChild size="lg" className="w-full justify-between group">
                  <Link to="/ContactPage">
                    <span>Book Free Consultation</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full">
                  <Link to="/SeoAuditsPage">
                    <span>Get a Free SEO Audit</span>
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full">
                  <Link to="/CaseStudiesPage">
                    <span>View Case Studies</span>
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}